import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AnagraficaOcrReviewField } from './anagrafica-ocr';
import {
    mapPdfJsTextItemToFragment,
    resolveOcrDisplayBoxes,
    type PdfTextFragment,
} from './ocr-pdf-text-snap';

/** 原件对照面板属性。 */
export interface ArchiveOcrDocumentPaneProps {
    /** 本地预览地址，离开时由状态层释放。 */
    fileUrl: string;
    /** 文件 MIME，用于区分 PDF 与图片。 */
    mimeType: string;
    /** 文件名。 */
    fileName: string;
    /** 需要在原件上定位的字段。 */
    fields: AnagraficaOcrReviewField[];
    /** 当前对位字段。 */
    activeFieldId: string | null;
    /** 对位序号；同一字段再次聚焦时递增，用于重新滚动。 */
    focusSeq?: number;
    /**
     * 点击原件区域后聚焦表单字段。
     * @param fieldId 餐厅字段名
     * @param options.scrollForm 原件点击时应滚右侧表单
     */
    onFocusField: (fieldId: string, options?: { scrollForm?: boolean }) => void;
}

/** PDF 渲染后的一页（不含随核对变化的高亮框）。 */
interface RenderedPdfPage {
    /** 页码，从 1 开始。 */
    page: number;
    /** 渲染后的图片。 */
    imageUrl: string;
    /** 相对 PDF 1x viewport 的缩放比，用于把 bbox 映射到渲染坐标。 */
    scale: number;
    /** 渲染画布宽度（px），用于把像素框换成相对图片的百分比。 */
    renderWidth: number;
    /** 渲染画布高度（px）。 */
    renderHeight: number;
}

/** 叠加在某一页上的高亮框（相对渲染画布的像素）。 */
export interface FieldHighlightBox {
    /** 对应餐厅字段名。 */
    fieldId: string;
    /** 距页图左边缘的像素。 */
    left: number;
    /** 距页图顶边缘的像素。 */
    top: number;
    /** 高亮宽度。 */
    width: number;
    /** 高亮高度。 */
    height: number;
}

/** 相对页图的百分比定位，适配 `img { width:100% }` 缩放。 */
export interface FieldHighlightBoxPercent {
    /** 对应餐厅字段名。 */
    fieldId: string;
    /** 距左边缘的百分比。 */
    leftPercent: number;
    /** 距顶边缘的百分比。 */
    topPercent: number;
    /** 宽度百分比。 */
    widthPercent: number;
    /** 高度百分比。 */
    heightPercent: number;
}

/** 相对 PDF 1× 的基础渲染倍率（侧栏约半宽时避免发糊）。 */
const OCR_PDF_BASE_SCALE = 2;
/** 渲染倍率上限，避免多页登记文件撑爆内存。 */
const OCR_PDF_MAX_SCALE = 3.5;
/** 计入清晰度的 DPR 上限。 */
const OCR_PDF_DPR_CAP = 2.5;

/**
 * [TEMP] 图片 OCR 后端 bbox 暂不准时强制用图片下方字段列表对位。
 * 后端校准后改为 `false` 即可恢复 0–1 叠框（见 `resolveImageOcrAnchorMode`）。
 */
export const TEMP_FORCE_IMAGE_OCR_FIELD_LIST = true;

/** 图片 OCR 原件对位展示模式。 */
export interface ImageOcrAnchorMode {
    /** 是否在图片上叠加 bbox 高亮框。 */
    showOverlay: boolean;
    /** 是否展示图片下方全量字段列表。 */
    showFieldList: boolean;
    /** 是否仅为缺框字段补列表（叠框开启时）。 */
    showMissingBoxList: boolean;
}

/**
 * 解析图片 OCR 对位展示：叠框或下方列表。
 * @param options.hasMappedBoxes 是否已有可映射的 bbox
 * @param options.forceFieldList 临时强制列表；默认读 TEMP_FORCE_IMAGE_OCR_FIELD_LIST
 */
export function resolveImageOcrAnchorMode(options: {
    hasMappedBoxes: boolean;
    forceFieldList?: boolean;
}): ImageOcrAnchorMode {
    /** 是否强制走下方列表（忽略后端 bbox）。 */
    const forceFieldList = options.forceFieldList ?? TEMP_FORCE_IMAGE_OCR_FIELD_LIST;
    if (forceFieldList) {
        return {
            showOverlay: false,
            showFieldList: true,
            showMissingBoxList: false,
        };
    }
    return {
        showOverlay: options.hasMappedBoxes,
        showFieldList: !options.hasMappedBoxes,
        showMissingBoxList: options.hasMappedBoxes,
    };
}

/**
 * 计算 PDF 预览渲染倍率：基础倍率 × 设备像素比，并限制上限。
 * @param devicePixelRatio 当前屏 DPR；异常时按 1
 */
export function resolveOcrPdfRenderScale(devicePixelRatio = 1): number {
    /** 合法 DPR，至少为 1。 */
    const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0
        ? devicePixelRatio
        : 1;
    /** 参与计算的 DPR（过高收益有限、内存陡增）。 */
    const cappedDpr = Math.min(dpr, OCR_PDF_DPR_CAP);
    return Math.min(OCR_PDF_BASE_SCALE * cappedDpr, OCR_PDF_MAX_SCALE);
}

/**
 * 按页缩放把识别字段 bbox 映射为叠加高亮框（渲染画布像素）。
 * @param fields 核对字段
 * @param pageNumber 页码
 * @param scale 渲染缩放比
 */
export function mapOcrFieldBoxes(
    fields: AnagraficaOcrReviewField[],
    pageNumber: number,
    scale: number,
): FieldHighlightBox[] {
    return fields
        .filter((field) => field.page === pageNumber && field.bbox)
        .map((field) => ({
            fieldId: field.fieldId,
            left: (field.bbox?.x || 0) * scale,
            top: (field.bbox?.y || 0) * scale,
            width: (field.bbox?.w || 0) * scale,
            height: (field.bbox?.h || 0) * scale,
        }));
}

/**
 * 把已解析的展示框映射到指定页的画布像素框。
 * @param displayBoxes 吸附/后端解析后的框
 * @param pageNumber 页码
 * @param scale 渲染缩放比
 */
export function mapResolvedOcrBoxes(
    displayBoxes: ReadonlyArray<{
        fieldId: string;
        page: number;
        bbox: { x: number; y: number; w: number; h: number };
    }>,
    pageNumber: number,
    scale: number,
): FieldHighlightBox[] {
    return displayBoxes
        .filter((box) => box.page === pageNumber)
        .map((box) => ({
            fieldId: box.fieldId,
            left: box.bbox.x * scale,
            top: box.bbox.y * scale,
            width: box.bbox.w * scale,
            height: box.bbox.h * scale,
        }));
}

/**
 * 把画布像素框转为相对页图的百分比，避免 CSS 拉伸后错位。
 * @param box 画布像素框
 * @param renderWidth 渲染宽度
 * @param renderHeight 渲染高度
 */
export function mapOcrFieldBoxToPercent(
    box: FieldHighlightBox,
    renderWidth: number,
    renderHeight: number,
): FieldHighlightBoxPercent {
    /** 防除零，异常尺寸时退回 0。 */
    const width = renderWidth > 0 ? renderWidth : 1;
    /** 防除零。 */
    const height = renderHeight > 0 ? renderHeight : 1;
    return {
        fieldId: box.fieldId,
        leftPercent: (box.left / width) * 100,
        topPercent: (box.top / height) * 100,
        widthPercent: (box.width / width) * 100,
        heightPercent: (box.height / height) * 100,
    };
}

/**
 * 图片 OCR：后端 bbox 为相对原图宽高的 0–1 小数（左上原点、y 向下），
 * 转为相对展示图的百分比。等价于 left = x × 显示宽、top = y × 显示高等。
 * @param fieldId 餐厅字段名
 * @param bbox 相对坐标 {x,y,w,h}，范围约 0–1
 */
export function mapNormalizedOcrBBoxToPercent(
    fieldId: string,
    bbox: { x: number; y: number; w: number; h: number },
): FieldHighlightBoxPercent {
    return {
        fieldId,
        leftPercent: bbox.x * 100,
        topPercent: bbox.y * 100,
        widthPercent: bbox.w * 100,
        heightPercent: bbox.h * 100,
    };
}

/**
 * 批量把图片相对坐标框转为百分比叠框。
 * @param displayBoxes 后端/解析后的框（图片无文字层吸附）
 */
export function mapNormalizedOcrBoxesToPercent(
    displayBoxes: ReadonlyArray<{
        fieldId: string;
        bbox: { x: number; y: number; w: number; h: number };
    }>,
): FieldHighlightBoxPercent[] {
    return displayBoxes.map((box) => mapNormalizedOcrBBoxToPercent(box.fieldId, box.bbox));
}

/**
 * 仅在左侧原件容器（`.archive-ocr-doc`）内滚动到目标，避免 `scrollIntoView` 带动整页或右侧表单。
 * @param target 要对齐的锚点（bbox 或列表行）
 */
export function scrollOcrDocTargetIntoView(target: Element): void {
    /** 原件侧栏可滚动容器。 */
    const container = target.closest('.archive-ocr-doc');
    if (!(container instanceof HTMLElement) || !(target instanceof HTMLElement)) {
        return;
    }
    /** 容器当前可视区域。 */
    const containerRect = container.getBoundingClientRect();
    /** 目标当前可视区域。 */
    const targetRect = target.getBoundingClientRect();
    /** 目标中心相对容器可视区中心的偏移。 */
    const delta =
        targetRect.top - containerRect.top - containerRect.height / 2 + targetRect.height / 2;
    container.scrollBy({ top: delta, behavior: 'smooth' });
}

/** 字段列表行属性（图片/无 bbox 时的对位锚点）。 */
interface ArchiveOcrDocFieldListProps {
    /** 需要展示的核对字段。 */
    fields: AnagraficaOcrReviewField[];
    /** 当前对位字段。 */
    activeFieldId: string | null;
    /** 点击行后聚焦表单字段（并滚右侧）。 */
    onFocusField: (fieldId: string, options?: { scrollForm?: boolean }) => void;
}

/**
 * 原件下方字段列表：对齐 anagrafica-v4 `.doc-r` 的「标签 | 值」布局，颜色走语义 token。
 * @param props 字段与对位回调
 */
function ArchiveOcrDocFieldList({
    fields,
    activeFieldId,
    onFocusField,
}: ArchiveOcrDocFieldListProps): JSX.Element {
    return (
        <Box className="archive-ocr-doc__list">
            {fields.map((field) => (
                <ButtonBase
                    key={field.fieldId}
                    className={field.fieldId === activeFieldId
                        ? 'archive-ocr-doc__row archive-ocr-doc__row--active'
                        : 'archive-ocr-doc__row'}
                    data-ocr-doc-field={field.fieldId}
                    title={field.fieldId}
                    onClick={() => onFocusField(field.fieldId, { scrollForm: true })}
                >
                    <Typography
                        variant="caption"
                        component="span"
                        className="archive-ocr-doc__row-key"
                    >
                        {field.sourceField || field.fieldId}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="span"
                        className="archive-ocr-doc__row-value"
                    >
                        {field.valueOcr}
                    </Typography>
                </ButtonBase>
            ))}
        </Box>
    );
}

/**
 * 渲染单个原件高亮框（PDF 页或图片共用）。
 * @param box 百分比定位
 * @param activeFieldId 当前对位字段
 * @param onFocusField 点击后聚焦表单
 */
function ArchiveOcrHighlightBox({
    box,
    activeFieldId,
    onFocusField,
}: {
    box: FieldHighlightBoxPercent;
    activeFieldId: string | null;
    onFocusField: (fieldId: string, options?: { scrollForm?: boolean }) => void;
}): JSX.Element {
    return (
        <ButtonBase
            className={box.fieldId === activeFieldId
                ? 'archive-ocr-doc__box archive-ocr-doc__box--active'
                : 'archive-ocr-doc__box'}
            data-ocr-doc-field={box.fieldId}
            title={box.fieldId}
            style={{
                left: `${box.leftPercent}%`,
                top: `${box.topPercent}%`,
                width: `${box.widthPercent}%`,
                height: `${box.heightPercent}%`,
            }}
            onClick={() => onFocusField(box.fieldId, { scrollForm: true })}
            aria-label={box.fieldId}
        />
    );
}

/**
 * 左侧原件预览。PDF 按页绘制并叠框（文字层吸附）；
 * 图片默认按 0–1 叠框，无坐标时用字段列表；[TEMP] 当前强制图片下方列表（见 TEMP_FORCE_IMAGE_OCR_FIELD_LIST）。
 * @param props 本地文件与核对字段
 */
export function ArchiveOcrDocumentPane({
    fileUrl,
    mimeType,
    fileName,
    fields,
    activeFieldId,
    focusSeq = 0,
    onFocusField,
}: ArchiveOcrDocumentPaneProps): JSX.Element {
    const { t } = useTranslation();
    /** PDF 各页渲染结果；图片模式保持为空。 */
    const [pages, setPages] = useState<RenderedPdfPage[]>([]);
    /** PDF 文字层碎片，用于吸附校准 AI bbox。 */
    const [textFragments, setTextFragments] = useState<PdfTextFragment[]>([]);
    /** PDF 渲染失败时退回浏览器内嵌预览加字段列表。 */
    const [pdfFailed, setPdfFailed] = useState(false);
    /** 是否按 PDF 处理。 */
    const isPdf = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

    // 仅随文件变化重渲 PDF；核对字段变化只更新叠加框，避免二次打开/确认时整份重渲失败变空白
    useEffect(() => {
        if (!isPdf || !fileUrl) {
            setPages([]);
            setTextFragments([]);
            return undefined;
        }
        let cancelled = false;
        const renderPdf = async () => {
            try {
                const pdfjs = await import('pdfjs-dist/build/pdf');
                const worker = await import('pdfjs-dist/build/pdf.worker.entry');
                pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default?: string }).default || worker;
                const documentTask = pdfjs.getDocument(fileUrl);
                const pdf = await documentTask.promise;
                const nextPages: RenderedPdfPage[] = [];
                /** 各页文字碎片汇总。 */
                const nextFragments: PdfTextFragment[] = [];
                /** 按屏 DPR 提高栅格分辨率，减轻侧栏放大后发糊。 */
                const renderScale = resolveOcrPdfRenderScale(
                    typeof window !== 'undefined' ? window.devicePixelRatio : 1,
                );
                for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                    const page = await pdf.getPage(pageNumber);
                    const viewport = page.getViewport({ scale: renderScale });
                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const context = canvas.getContext('2d');
                    if (!context) {
                        continue;
                    }
                    // 高分屏栅格缩到侧栏宽度时保持平滑
                    context.imageSmoothingEnabled = true;
                    context.imageSmoothingQuality = 'high';
                    await page.render({ canvasContext: context, viewport } as never).promise;
                    // JPEG 在高倍率下体积远小于 PNG，文字登记件 0.92 观感接近直开
                    const imageUrl = canvas.toDataURL('image/jpeg', 0.92);
                    const baseViewport = page.getViewport({ scale: 1 });
                    const scale = viewport.width / baseViewport.width;
                    nextPages.push({
                        page: pageNumber,
                        imageUrl,
                        scale,
                        renderWidth: viewport.width,
                        renderHeight: viewport.height,
                    });
                    // 抽文字层：经 viewport 转为页内左上（含 crop 偏移），与画布叠框一致
                    const textContent = await page.getTextContent();
                    for (const rawItem of textContent.items) {
                        const fragment = mapPdfJsTextItemToFragment(
                            rawItem as { str?: string; width?: number; height?: number; transform?: number[] },
                            pageNumber,
                            baseViewport,
                        );
                        if (fragment) {
                            nextFragments.push(fragment);
                        }
                    }
                }
                if (!cancelled) {
                    setPages(nextPages);
                    setTextFragments(nextFragments);
                    setPdfFailed(false);
                }
            } catch {
                if (!cancelled) {
                    setPdfFailed(true);
                    setPages([]);
                    setTextFragments([]);
                }
            }
        };
        void renderPdf();
        return () => {
            cancelled = true;
        };
    }, [fileUrl, isPdf]);

    /** 吸附后的展示框；图片无文字层，直接用后端 0–1 bbox。 */
    const displayBoxes = useMemo(
        () => resolveOcrDisplayBoxes(fields, isPdf ? textFragments : []),
        [fields, textFragments, isPdf],
    );
    /** 当前是否有可映射高亮框（含图片临时关闭叠框前的原始结果）。 */
    const hasMappedBoxes = displayBoxes.length > 0;
    /** 图片对位模式：临时可强制下方列表，避免不准 bbox。 */
    const imageAnchorMode = useMemo(
        () => resolveImageOcrAnchorMode({ hasMappedBoxes }),
        [hasMappedBoxes],
    );
    /** 图片模式下的百分比叠框；强制列表时为空。 */
    const imageHighlightBoxes = useMemo(
        () => (isPdf || !imageAnchorMode.showOverlay
            ? []
            : mapNormalizedOcrBoxesToPercent(displayBoxes)),
        [isPdf, imageAnchorMode.showOverlay, displayBoxes],
    );
    /**
     * 无可用高亮时用字段列表对位：
     * PDF 失败或渲完仍无框；图片按 resolveImageOcrAnchorMode（可临时强制列表）。
     */
    const showFieldList = isPdf
        ? (pdfFailed || (pages.length > 0 && !hasMappedBoxes))
        : imageAnchorMode.showFieldList;
    /**
     * 已有叠框且能真正叠在页图上时，为缺框字段保留列表锚点。
     * PDF 渲失败走 iframe + 全量列表；图片强制列表时不重复补缺框列表。
     */
    const fieldsMissingBox = isPdf
        ? (hasMappedBoxes && !pdfFailed
            ? fields.filter((field) => !displayBoxes.some((box) => box.fieldId === field.fieldId))
            : [])
        : (imageAnchorMode.showMissingBoxList
            ? fields.filter((field) => !displayBoxes.some((box) => box.fieldId === field.fieldId))
            : []);

    // PDF 页或列表锚点就绪后再滚；只动左侧容器，不带动整页/右侧表单
    useEffect(() => {
        if (!activeFieldId) {
            return undefined;
        }
        /** 当前对位的原件锚点（bbox 或列表行）。 */
        const target = document.querySelector(`[data-ocr-doc-field="${activeFieldId}"]`);
        if (!target) {
            return undefined;
        }
        scrollOcrDocTargetIntoView(target);
        return undefined;
    }, [activeFieldId, focusSeq, pages, showFieldList, fieldsMissingBox.length, displayBoxes, imageHighlightBoxes]);

    return (
        <Box className="archive-ocr-doc">
            <Typography variant="caption" className="archive-ocr-doc__name">{fileName}</Typography>
            {isPdf && !pdfFailed && pages.length > 0 ? pages.map((page) => {
                const boxes = mapResolvedOcrBoxes(displayBoxes, page.page, page.scale)
                    .map((box) => mapOcrFieldBoxToPercent(box, page.renderWidth, page.renderHeight));
                return (
                    <Box key={page.page} className="archive-ocr-doc__page">
                        <img src={page.imageUrl} alt={t('archive_form_ocr_document_page', { page: page.page })} />
                        {boxes.map((box) => (
                            <ArchiveOcrHighlightBox
                                key={box.fieldId}
                                box={box}
                                activeFieldId={activeFieldId}
                                onFocusField={onFocusField}
                            />
                        ))}
                    </Box>
                );
            }) : null}
            {!isPdf && fileUrl ? (
                <Box className="archive-ocr-doc__page archive-ocr-doc__page--image">
                    <img className="archive-ocr-doc__image" src={fileUrl} alt={fileName} />
                    {imageHighlightBoxes.map((box) => (
                        <ArchiveOcrHighlightBox
                            key={box.fieldId}
                            box={box}
                            activeFieldId={activeFieldId}
                            onFocusField={onFocusField}
                        />
                    ))}
                </Box>
            ) : null}
            {isPdf && pdfFailed ? (
                <iframe className="archive-ocr-doc__frame" title={fileName} src={fileUrl} />
            ) : null}
            {showFieldList ? (
                <ArchiveOcrDocFieldList
                    fields={fields}
                    activeFieldId={activeFieldId}
                    onFocusField={onFocusField}
                />
            ) : null}
            {fieldsMissingBox.length > 0 ? (
                <ArchiveOcrDocFieldList
                    fields={fieldsMissingBox}
                    activeFieldId={activeFieldId}
                    onFocusField={onFocusField}
                />
            ) : null}
        </Box>
    );
}
